import type {
  CellStyle,
  ColDefField,
  GetRowIdParams,
  GridOptions,
  RowClassParams
} from 'ag-grid-community';

import {
  type BaseTreeNode,
  DEVICE_TREE_LEVEL,
  type RowClassConfig,
  type SharedTreeGridConfig
} from './DevicesTable.types';

const DEVICE_ROW_CLASS_CONFIG: RowClassConfig = {
  levelClassMap: {
    [DEVICE_TREE_LEVEL.SOURCE]: 'device-main-table-level-1',
    [DEVICE_TREE_LEVEL.PROVIDER]: 'device-main-table-level-2'
  },
  lastChildClass: 'device-main-table-row-is-last',
  selectedRowClass: 'ag-grid-device-main-table-row-selected',
  detailOpenRowClass: 'ag-grid-device-main-table-row-detail-open'
};

/**
 * Generates CSS classes for tree table rows based on level and selection state
 * @template T - Tree node type extending BaseTreeNode
 * @param params - AG Grid row class params
 * @param config - Configuration for row class styling
 * @returns Array of CSS class names to apply to the row
 */
export const getTreeRowClass = <T extends BaseTreeNode>(
  params: RowClassParams<T>,
  config: RowClassConfig
): string[] => {
  const classes: string[] = [];
  const { level } = params.node;

  // Add level-specific class if mapping exists
  if (level !== null && level !== undefined && config.levelClassMap[level]) {
    classes.push(config.levelClassMap[level]);
  }

  // Add last child class
  if (params.node.lastChild) {
    classes.push(config.lastChildClass);
  }

  // Add selected class
  if (params.node.isSelected()) {
    classes.push(config.selectedRowClass);
  }

  return classes;
};

/**
 * Get row class for device table styling
 */
export const getDeviceRowClass = (params: RowClassParams): string => {
  return getTreeRowClass(params, DEVICE_ROW_CLASS_CONFIG).join(' ');
};

/**
 * Creates base grid options for tree data grids with common configuration
 * @template T - Tree node type extending BaseTreeNode
 * @param config - Configuration object for customizing grid behavior
 * @returns GridOptions configured for tree data display
 */
export const getSharedTreeGridOptions = <T extends BaseTreeNode>(
  config: SharedTreeGridConfig<T>
): GridOptions<T> => {
  const {
    autoGroupField,
    autoGroupHeaderName,
    innerRenderer,
    suppressExpandIcon,
    autoGroupMinWidth = 300,
    autoGroupFlex = 2,
    checkboxSelection = false,
    headerCheckboxSelection = false
  } = config;

  return {
    treeData: true,
    animateRows: true,
    groupDefaultExpanded: 0,
    suppressContextMenu: true,
    suppressDragLeaveHidesColumns: true,

    getDataPath: (data: T) => data.path,

    autoGroupColumnDef: {
      field: autoGroupField as unknown as ColDefField<T>,
      headerName: autoGroupHeaderName,
      flex: autoGroupFlex,
      minWidth: autoGroupMinWidth,
      sortable: true,
      suppressMovable: true,
      lockPosition: 'left',
      resizable: true,
      cellRenderer: 'agGroupCellRenderer',
      checkboxSelection,
      headerCheckboxSelection,

      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: false,
        suppressEnterExpand: false,
        innerRenderer,
        suppressExpandIcon
      }
    },

    defaultColDef: {
      sortable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      resizable: true,
      cellStyle: (params: { colDef: { field?: string } }): CellStyle => {
        if (!params.colDef.field) return {};
        return { cursor: 'pointer' };
      }
    },

    pagination: true,
    rowSelection: 'single',
    suppressRowClickSelection: true,
    suppressCellFocus: true,

    getRowId: (params: GetRowIdParams<T>) => String(params.data.id)
  };
};
